import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Instagram, Twitter, Facebook, Copy, Check, Link2, Hourglass } from 'lucide-react';
import { useContagemRegressiva } from '../../hooks/useContagemRegressiva';
import { formatarContagem, nivelUrgencia } from '../../utils/formatarContagem';

const ESTILO_POR_NIVEL = {
  calmo: { caixa: 'bg-blue-50 border-blue-200', texto: 'text-blue-800', icone: 'text-blue-500' },
  atencao: { caixa: 'bg-amber-50 border-amber-200', texto: 'text-amber-800', icone: 'text-amber-500' },
  urgente: { caixa: 'bg-red-50 border-red-200', texto: 'text-red-800', icone: 'text-red-500' },
  critico: { caixa: 'bg-red-50 border-red-300', texto: 'text-red-800', icone: 'text-red-500' }
};

const CONVITE_POR_NIVEL = {
  calmo: 'Ainda dá tempo — compartilhe e ajude a alcançar mais gente!',
  atencao: 'A reta final está chegando — compartilhe agora!',
  urgente: 'Últimos minutos! Compartilhe rápido antes que feche.',
  critico: 'Faltam só segundos! Compartilhe agora mesmo.'
};

// lucide-react não tem ícone de marca do WhatsApp (só os genéricos tipo
// MessageCircle) — glifo oficial embutido aqui em vez de um substituto
// vago, mesmo path usado por bibliotecas de ícone de marca (Simple Icons).
const IconeWhatsapp = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12.001 2c-5.523 0-10 4.477-10 10 0 1.766.46 3.484 1.334 5.001L2 22l5.135-1.319A9.955 9.955 0 0012.001 22c5.523 0 10-4.477 10-10s-4.477-10-10-10zm0 18.16c-1.617 0-3.198-.436-4.577-1.261l-.328-.194-3.048.783.813-2.977-.213-.34A8.13 8.13 0 013.84 12c0-4.502 3.658-8.16 8.16-8.16 4.502 0 8.16 3.658 8.16 8.16 0 4.502-3.658 8.16-8.161 8.16z" />
  </svg>
);

// Convite pra compartilhar logo depois de votar — só faz sentido enquanto a
// enquete ainda está recebendo voto (chamar mais gente pra uma enquete já
// encerrada não ajuda em nada), por isso só é montado no cenário
// jaVotou && !jaEncerrada em FormularioDinamico.js. Instagram não tem URL
// pública de "compartilhar link" como as outras redes — só copia o link e
// avisa pra colar manualmente (stories, bio ou direct).
const CompartilharEnquete = ({ titulo, dataEncerramento }) => {
  const [copiado, setCopiado] = useState(false);
  const url = window.location.href;
  const mensagem = `Vote na enquete "${titulo}" da Prefeitura de Corumbá!`;

  // Contagem regressiva até o encerramento — sem data configurada (enquete
  // fecha só manualmente pelo admin) ou já tendo virado zero enquanto a
  // pessoa estava na tela, a faixa simplesmente não aparece (ver `nivel`
  // abaixo); não faz sentido nenhum mostrar "faltam 0 segundos" parado.
  const restante = useContagemRegressiva(dataEncerramento);
  const nivel = nivelUrgencia(restante);
  const textoContagem = formatarContagem(restante);

  const abrir = (link) => window.open(link, '_blank', 'noopener,noreferrer');

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      toast.error('Não foi possível copiar o link. Copie manualmente.');
    }
  };

  const compartilharWhatsapp = () =>
    abrir(`https://wa.me/?text=${encodeURIComponent(`${mensagem} ${url}`)}`);

  const compartilharTwitter = () =>
    abrir(`https://twitter.com/intent/tweet?text=${encodeURIComponent(mensagem)}&url=${encodeURIComponent(url)}`);

  const compartilharFacebook = () =>
    abrir(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);

  const compartilharInstagram = async () => {
    await copiarLink();
    toast('Cole esse link no Instagram (stories, bio ou direct) — a rede não permite compartilhar direto pelo navegador.', { icon: '📋', duration: 5000 });
  };

  const REDES = [
    { nome: 'WhatsApp', icone: IconeWhatsapp, cor: 'text-green-600 bg-green-50 hover:bg-green-100', acao: compartilharWhatsapp },
    { nome: 'Instagram', icone: Instagram, cor: 'text-pink-600 bg-pink-50 hover:bg-pink-100', acao: compartilharInstagram },
    { nome: 'X (Twitter)', icone: Twitter, cor: 'text-gray-900 bg-gray-100 hover:bg-gray-200', acao: compartilharTwitter },
    { nome: 'Facebook', icone: Facebook, cor: 'text-blue-600 bg-blue-50 hover:bg-blue-100', acao: compartilharFacebook }
  ];

  return (
    <div className="pt-6 border-t border-gray-100">
      {nivel && (
        <div
          className={`flex items-start gap-3 rounded-xl border px-4 py-3 mb-4 text-left ${ESTILO_POR_NIVEL[nivel].caixa} ${nivel === 'critico' ? 'animate-pulse' : ''}`}
        >
          <Hourglass className={`w-5 h-5 flex-shrink-0 mt-0.5 ${ESTILO_POR_NIVEL[nivel].icone}`} />
          <div>
            <p className={`text-sm font-bold ${ESTILO_POR_NIVEL[nivel].texto}`}>{textoContagem} para encerrar</p>
            <p className={`text-xs mt-0.5 ${ESTILO_POR_NIVEL[nivel].texto}`}>{CONVITE_POR_NIVEL[nivel]}</p>
          </div>
        </div>
      )}

      <p className="text-center text-sm font-medium text-gray-700 mb-3">Convide mais gente pra participar!</p>

      <div className="flex items-center justify-center gap-3 mb-4">
        {REDES.map(({ nome, icone: Icone, cor, acao }) => (
          <button
            key={nome}
            type="button"
            onClick={acao}
            title={`Compartilhar no ${nome}`}
            aria-label={`Compartilhar no ${nome}`}
            className={`w-11 h-11 flex items-center justify-center rounded-full transition-colors ${cor}`}
          >
            <Icone className="w-5 h-5" />
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 max-w-sm mx-auto">
        <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          type="text"
          readOnly
          value={url}
          onFocus={(e) => e.target.select()}
          className="flex-1 min-w-0 bg-transparent text-xs text-gray-600 truncate outline-none"
        />
        <button
          type="button"
          onClick={copiarLink}
          className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md flex-shrink-0 transition-colors ${
            copiado ? 'bg-emerald-50 text-emerald-700' : 'bg-primary-600 text-white hover:bg-primary-700'
          }`}
        >
          {copiado ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copiado ? 'Copiado' : 'Copiar'}
        </button>
      </div>
    </div>
  );
};

export default CompartilharEnquete;
